import { collection, query, where, onSnapshot, type DocumentData } from 'firebase/firestore';
import { db } from '../../config';
import { handleFirebaseError } from '../../error-handling';
import { chunkIds, groupBy } from '../batchedIn';

/** A customer's most recent exam: its date and status ('pending' without exams). */
export interface LastExam {
  lastExam?: Date;
  lastExamStatus: string;
}

/**
 * Pure: the newest of one customer's clinicalExams rows. Throws on a row
 * whose date is not a Timestamp, as the per-customer listener did.
 */
export function lastExamOf(rows: DocumentData[]): LastExam {
  if (rows.length === 0) {
    // If no exams exist, set status as pending
    return { lastExam: undefined, lastExamStatus: 'pending' };
  }

  // Sort exams by date to get the most recent
  const exams = rows
    .map(data => ({
      date: data.date.toDate() as Date,
      status: data.status || 'pending'
    }))
    .sort((a, b) => b.date.getTime() - a.date.getTime());

  return { lastExam: exams[0].date, lastExamStatus: exams[0].status };
}

/**
 * Live latest exam for a roster (#117): one listener per 30 customers
 * (`customerId in`) instead of one per customer. Each snapshot reports every
 * customer of its chunk at once; a customer whose rows cannot be read is
 * logged and left out of that update. Returns one unsubscribe for all.
 */
export function subscribeToCustomersExams(
  customerIds: string[],
  onUpdate: (updates: Map<string, LastExam>) => void,
  onError?: (error: Error) => void
): () => void {
  const unsubscribes = chunkIds(customerIds).map((part) =>
    onSnapshot(
      query(collection(db, 'clinicalExams'), where('customerId', 'in', part)),
      (snapshot) => {
        const rowsByCustomer = groupBy(snapshot.docs.map(doc => doc.data()), 'customerId');
        const updates = new Map<string, LastExam>();
        for (const customerId of part) {
          try {
            updates.set(customerId, lastExamOf(rowsByCustomer.get(customerId) ?? []));
          } catch (error) {
            console.error(`Error reading exams for customer ${customerId}:`, error);
          }
        }
        onUpdate(updates);
      },
      (error) => {
        console.error('Error subscribing to exams:', error);
        handleFirebaseError(error);
        if (onError) onError(error);
      }
    )
  );
  return () => unsubscribes.forEach(unsubscribe => unsubscribe());
}
